'use client'

import { cn } from '@brew/ui/cn'
import {
  getPlainText,
  type MentionItem,
  type MentionSegment,
  type Segment,
  SmartTextbox,
} from '@tigerabrodioss/fude'
import { MailIcon, WorkflowIcon } from 'lucide-react'
import type {
  ChangeEvent,
  ChangeEventHandler,
  ClipboardEventHandler,
  KeyboardEvent,
  DragEvent as ReactDragEvent,
} from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FUDE_MENTION_DROPDOWN_SELECTOR,
  FUDE_PROMPT_INPUT_CLASSNAMES,
  FUDE_PROMPT_INPUT_STYLES,
  syncFudeMentionDropdown,
} from '@/components/ai-elements/fude-theme'
import {
  type DictationHold,
  type DictationTarget,
  usePromptInputDictation,
} from '@/components/ai-elements/prompt-input/dictation-context'
import {
  PROMPT_INPUT_PLAIN_TEXTAREA_CLASSNAME,
  PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME,
  PROMPT_INPUT_TEXTAREA_HEIGHT_CAP_CLASSNAME,
} from '@/components/ai-elements/prompt-input-metrics'
import {
  ARTIFACT_MENTION_DRAG_MIME,
  type ArtifactMentionPayload,
  parseArtifactMentionPayload,
} from '@/components/chat/artifact-mention'
import { InputGroupTextarea } from '@/components/ui/input-group'
import { TextShimmer } from '@/components/ui/text-shimmer'
import type { GetPromptSuggestionContext } from '@/hooks/use-prompt-suggestion-context'
import { endsWithTerminalPunctuation } from '@/lib/prompt-suggestions/context'
import type {
  AttachmentsContext,
  PromptInputMentionItem,
  TextInputContext,
} from './prompt-input'

type FudePromptInputTextareaProps = {
  attachments: AttachmentsContext
  controller: TextInputContext | null
  className?: string
  disabled?: boolean
  /**
   * When false, no inline autocomplete suggestions are fetched. Used to
   * gate the `/api/prompt-autocomplete` endpoint so unauthenticated
   * surfaces (public homepage, public template remix) cannot trigger
   * server-side LLM calls.
   */
  enableAutocomplete?: boolean
  /** Increment to focus the editable surface and place the caret at the end. */
  focusRequestKey?: number
  /**
   * Identity-stable getter (see `usePromptSuggestionContext`) invoked at
   * fetch time — after the debounce, never per keystroke — to attach
   * surface/account/conversation context to the suggestion request.
   */
  getSuggestionContext?: GetPromptSuggestionContext
  onChange?: ChangeEventHandler<HTMLTextAreaElement>
  onFetchMentions?:
    | ((query: string) => Promise<Array<PromptInputMentionItem>>)
    | ((query: string) => Array<PromptInputMentionItem>)
  placeholder?: string
  shimmerPlaceholder?: boolean
  value?: string
  'data-testid'?: string
  'aria-label'?: string
}

function createTextSegments(value: string): Array<Segment> {
  if (!value) {
    return []
  }
  return [{ type: 'text', value }]
}

function createSyntheticChangeEvent(
  value: string
): ChangeEvent<HTMLTextAreaElement> {
  return {
    currentTarget: { value } as HTMLTextAreaElement,
    target: { value } as HTMLTextAreaElement,
  } as ChangeEvent<HTMLTextAreaElement>
}

function appendMentionSegment(
  currentSegments: Array<Segment>,
  item: MentionItem
): Array<Segment> {
  const nextSegments = [...currentSegments]
  const lastSegment = nextSegments.at(-1)

  if (lastSegment?.type === 'text') {
    const shouldInsertSeparator =
      lastSegment.value.length > 0 && !/\s$/.test(lastSegment.value)
    nextSegments[nextSegments.length - 1] = {
      type: 'text',
      value: `${lastSegment.value}${shouldInsertSeparator ? ' ' : ''}`,
    }
  } else if (nextSegments.length > 0) {
    nextSegments.push({ type: 'text', value: ' ' })
  }

  nextSegments.push({
    type: 'mention',
    item,
  } satisfies MentionSegment)
  nextSegments.push({ type: 'text', value: ' ' })

  return nextSegments
}

/** Whether the caret is collapsed with only whitespace after it. */
function isCaretAtEnd(editable: HTMLElement): boolean {
  const selection = document.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return true
  }
  const range = selection.getRangeAt(0)
  if (!editable.contains(range.endContainer)) {
    return true
  }
  if (!range.collapsed) {
    return false
  }
  const tail = document.createRange()
  tail.selectNodeContents(editable)
  tail.setStart(range.endContainer, range.endOffset)
  return tail.toString().replace(/[\s\u200b\ufeff]/g, '') === ''
}

/**
 * The prompt as it was when dictation began, followed by everything dictated
 * so far. Chips in `base` survive untouched; the dictated text joins the
 * trailing text segment with a single space.
 */
function appendDictatedText(
  base: Array<Segment>,
  dictated: string
): Array<Segment> {
  const text = dictated.trim()
  if (!text) {
    return base
  }
  const baseText = getPlainText(base)
  const separator = baseText.length > 0 && !/\s$/.test(baseText) ? ' ' : ''
  const last = base.at(-1)
  if (last?.type === 'text') {
    return [
      ...base.slice(0, -1),
      { type: 'text', value: `${last.value}${separator}${text}` },
    ]
  }
  return [...base, { type: 'text', value: `${separator}${text}` }]
}

function createArtifactMentionItem(
  payload: ArtifactMentionPayload
): MentionItem {
  return {
    id: `${payload.kind}:${payload.entityId}`,
    searchValue: payload.searchValue,
    label: payload.title,
    icon:
      payload.kind === 'email' ? (
        <MailIcon className="size-3.5" />
      ) : (
        <WorkflowIcon className="size-3.5" />
      ),
  }
}

function isValidArtifactMentionDropTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false
  }

  if (target.closest('[data-testid="chat-workspace-panel"]')) {
    return false
  }

  return target.closest('[data-testid="chat-page"]') !== null
}

export function focusFudeEditableAtEnd(root: HTMLElement | null): void {
  const editable = root?.querySelector<HTMLElement>('[contenteditable="true"]')
  if (!editable) {
    return
  }

  editable.focus()
  const selection = window.getSelection()
  if (!selection) {
    return
  }
  const range = document.createRange()
  range.selectNodeContents(editable)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

export function FudePromptInputTextarea({
  attachments,
  controller,
  className,
  disabled = false,
  enableAutocomplete = true,
  focusRequestKey,
  getSuggestionContext,
  onChange,
  onFetchMentions,
  placeholder = 'What would you like to know?',
  shimmerPlaceholder = false,
  value,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: FudePromptInputTextareaProps) {
  const externalValue = value ?? controller?.value ?? ''
  const [segments, setSegments] = useState<Array<Segment>>(() =>
    createTextSegments(externalValue)
  )
  const externalValueRef = useRef(externalValue)
  const mentionCacheRef = useRef(new Map<string, Array<MentionItem>>())
  const hiddenTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const plainText = useMemo(() => getPlainText(segments), [segments])
  const [isMentionsLoading, setIsMentionsLoading] = useState(false)
  const dictation = usePromptInputDictation()
  const isDictating = dictation !== null && dictation.status !== 'idle'
  // The prompt as it was when dictation began; null outside dictation.
  const dictationBaseRef = useRef<Array<Segment> | null>(null)
  const segmentsRef = useRef(segments)
  const isDictatingRef = useRef(isDictating)
  const rebaseDictationRef = useRef(dictation?.rebase)
  const dictationRef = useRef(dictation)
  /** Held while the user's caret sits mid-text during dictation. */
  const caretHoldRef = useRef<DictationHold | null>(null)
  // Every prompt writer except dictation itself (typing, a mention chip, a
  // new value from the parent): what the prompt now shows is the base new
  // speech appends to, or the next transcript frame would wipe the change.
  const rebaseDictationOnto = useCallback((nextSegments: Array<Segment>) => {
    if (dictationBaseRef.current !== null) {
      dictationBaseRef.current = nextSegments
      rebaseDictationRef.current?.()
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Ultracite baseline — verify deps when touching this hook
  useEffect(() => {
    mentionCacheRef.current.clear()
  }, [onFetchMentions])

  useEffect(() => {
    syncFudeMentionDropdown(isMentionsLoading)

    if (typeof document === 'undefined') {
      return
    }

    const observer = new MutationObserver(() => {
      syncFudeMentionDropdown(isMentionsLoading)
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => observer.disconnect()
  }, [isMentionsLoading])

  useEffect(() => {
    if (externalValueRef.current === externalValue) {
      return
    }
    externalValueRef.current = externalValue

    if (externalValue !== plainText) {
      const nextSegments = createTextSegments(externalValue)
      setSegments(nextSegments)
      rebaseDictationOnto(nextSegments)
      // Sync the provider's internal textInput state so form submission
      // reads the correct value even when the change originated externally
      // (e.g. prompt card selection) rather than from user typing.
      controller?.setInput(externalValue)
    }
  }, [controller, externalValue, plainText, rebaseDictationOnto])

  useEffect(() => {
    if (focusRequestKey === undefined) {
      return
    }
    const frame = requestAnimationFrame(() => {
      focusFudeEditableAtEnd(rootRef.current)
    })
    return () => cancelAnimationFrame(frame)
  }, [focusRequestKey])

  const emitPlainTextChange = useCallback(
    (nextPlainText: string) => {
      controller?.setInput(nextPlainText)
      onChange?.(createSyntheticChangeEvent(nextPlainText))
    },
    [controller, onChange]
  )

  const emitPlainTextChangeRef = useRef(emitPlainTextChange)

  const handleSegmentsChange = useCallback(
    (nextSegments: Array<Segment>) => {
      setSegments(nextSegments)
      // Fude reports only USER edits here (a programmatic value change never
      // calls back).
      rebaseDictationOnto(nextSegments)
      emitPlainTextChange(getPlainText(nextSegments))
    },
    [emitPlainTextChange, rebaseDictationOnto]
  )

  // Stable for the editor's lifetime; everything it reads is a ref.
  const dictationTarget = useMemo<DictationTarget>(
    () => ({
      begin() {
        dictationBaseRef.current = segmentsRef.current
      },
      render(text) {
        const base = dictationBaseRef.current
        if (base === null) {
          return
        }
        const nextSegments = appendDictatedText(base, text)
        setSegments(nextSegments)
        emitPlainTextChangeRef.current(getPlainText(nextSegments))
      },
      end() {
        dictationBaseRef.current = null
        caretHoldRef.current = null
      },
    }),
    []
  )

  // Each transcript frame re-renders the prompt, and the editor moves a
  // focused caret to the end when it does. While the user edits mid-text,
  // new speech waits (a hold) and lands once the caret is back at the end,
  // the editor loses focus, or dictation stops.
  const syncCaretHold = useCallback(() => {
    const editable = rootRef.current?.querySelector<HTMLElement>(
      '[contenteditable="true"]'
    )
    const isEditingMidText =
      isDictatingRef.current &&
      editable != null &&
      document.activeElement === editable &&
      !isCaretAtEnd(editable)
    if (isEditingMidText) {
      caretHoldRef.current ??= dictationRef.current?.hold('caret') ?? null
      return
    }
    const held = caretHoldRef.current
    caretHoldRef.current = null
    held?.release()
  }, [])

  const releaseCaretHold = useCallback(() => {
    const held = caretHoldRef.current
    caretHoldRef.current = null
    held?.release()
  }, [])

  const registerDictationTarget = dictation?.registerTarget
  // The editor is the dictation target only while it is mounted (the
  // disabled read-only box during a streaming turn is not). A ref callback
  // with cleanup keeps that tied to the DOM node, not to an effect.
  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      // SmartTextbox does not forward ARIA props to its editable node.
      const editable = node?.querySelector(
        '[role="textbox"][contenteditable="true"]'
      )
      if (ariaLabel) {
        editable?.setAttribute('aria-label', ariaLabel)
      } else {
        editable?.removeAttribute('aria-label')
      }
      if (!(node && registerDictationTarget)) {
        return
      }
      registerDictationTarget(dictationTarget)
      // The side chat panel collapses to zero width but stays mounted: stop
      // listening so audio never streams from a composer nobody can see.
      const observer =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver((entries) => {
              const width = entries[0]?.contentRect.width
              if (width === 0 && isDictatingRef.current) {
                dictationRef.current?.stop()
              }
            })
      observer?.observe(node)
      return () => {
        observer?.disconnect()
        rootRef.current = null
        registerDictationTarget(null)
      }
    },
    [ariaLabel, dictationTarget, registerDictationTarget]
  )

  const insertArtifactMention = useCallback(
    (payload: ArtifactMentionPayload) => {
      const mentionItem = createArtifactMentionItem(payload)

      setSegments((currentSegments) => {
        const nextSegments = appendMentionSegment(currentSegments, mentionItem)
        rebaseDictationOnto(nextSegments)
        emitPlainTextChange(getPlainText(nextSegments))
        return nextSegments
      })

      requestAnimationFrame(() => {
        rootRef.current
          ?.querySelector<HTMLElement>('[contenteditable="true"]')
          ?.focus()
      })
    },
    [emitPlainTextChange, rebaseDictationOnto]
  )

  const handleFetchMentions = useCallback(
    async (query: string) => {
      const cacheKey = query.trim().toLowerCase()
      const cached = mentionCacheRef.current.get(cacheKey)
      if (cached) {
        return cached
      }

      setIsMentionsLoading(true)

      try {
        const suggestions = onFetchMentions ? await onFetchMentions(query) : []
        const mapped = suggestions.map((suggestion) => ({
          id: suggestion.id,
          label: suggestion.label,
          searchValue: suggestion.searchValue,
          icon: suggestion.icon,
          tooltip: suggestion.tooltip,
        }))

        mentionCacheRef.current.set(cacheKey, mapped)
        return mapped
      } finally {
        setIsMentionsLoading(false)
      }
    },
    [onFetchMentions]
  )

  const suggestionCacheRef = useRef(new Map<string, Array<string>>())
  const getSuggestionContextRef = useRef(getSuggestionContext)
  useEffect(() => {
    getSuggestionContextRef.current = getSuggestionContext
    segmentsRef.current = segments
    isDictatingRef.current = isDictating
    emitPlainTextChangeRef.current = emitPlainTextChange
    rebaseDictationRef.current = dictation?.rebase
    dictationRef.current = dictation
  })

  const handleFetchSuggestions = useCallback(async (trailingText: string) => {
    // Dictation owns the prompt: no ghost text while speech is landing — a
    // debounce timer armed just before the mic was pressed must not fire.
    if (isDictatingRef.current) {
      return []
    }
    // Cheap client gates — no request when the input is too short to
    // carry intent, or ends like a finished sentence (the most common
    // "nothing to add" shape). The server re-checks both.
    if (trailingText.trim().length < 8) {
      return []
    }
    if (endsWithTerminalPunctuation(trailingText)) {
      return []
    }

    const suggestionContext = getSuggestionContextRef.current?.() ?? null
    // NUL separates the two halves of the key because it is the one
    // character that cannot appear in either half — a printable delimiter
    // like `:` can occur inside `trailingText` and would collide two
    // distinct prompts onto one cache entry. Written as the escape, never
    // as a literal NUL byte: a raw one makes `file(1)` report this source
    // as binary, and grep then skips the whole file silently.
    const cacheKey = `${suggestionContext?.cacheKey ?? ''}\0${trailingText
      .trim()
      .toLowerCase()}`
    const cached = suggestionCacheRef.current.get(cacheKey)
    if (cached) {
      return cached
    }

    const response = await fetch('/api/prompt-autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trailingText,
        ...(suggestionContext && { context: suggestionContext.context }),
      }),
    })

    const payload = (await response.json()) as {
      suggestions?: Array<string>
    }
    // Dictation started while the request was in flight.
    if (isDictatingRef.current) {
      return []
    }
    const suggestions = Array.isArray(payload.suggestions)
      ? payload.suggestions
      : []

    // Cache empty results too — "nothing to suggest" is a first-class
    // answer and must not re-fire the model on every debounce tick.
    suggestionCacheRef.current.set(cacheKey, suggestions)
    if (suggestionCacheRef.current.size > 50) {
      const firstKey = suggestionCacheRef.current.keys().next().value
      if (firstKey !== undefined) {
        suggestionCacheRef.current.delete(firstKey)
      }
    }

    return suggestions
  }, [])

  const requestSubmit = useCallback(() => {
    hiddenTextareaRef.current?.form?.requestSubmit()
  }, [])

  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || event.nativeEvent.isComposing) {
        return
      }

      if (
        event.key === 'Backspace' &&
        plainText === '' &&
        attachments.files.length > 0
      ) {
        event.preventDefault()
        event.stopPropagation()
        const lastAttachment = attachments.files.at(-1)
        if (lastAttachment) {
          attachments.remove(lastAttachment.id)
        }
        return
      }

      if (event.key !== 'Enter') {
        return
      }

      const isMentionDropdownOpen =
        typeof document !== 'undefined' &&
        document.querySelector(FUDE_MENTION_DROPDOWN_SELECTOR) !== null

      if (isMentionDropdownOpen || event.shiftKey) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      requestSubmit()
    },
    [attachments, disabled, plainText, requestSubmit]
  )

  // Pasted text is NEVER converted into a file attachment — heuristic
  // content detection false-positived on plain-prose prompts and hijacked
  // them into uploads. Only real clipboard files (images, copied files)
  // attach; everything else falls through as text.
  const handlePasteCapture = useCallback<ClipboardEventHandler<HTMLDivElement>>(
    (event) => {
      if (disabled) {
        return
      }

      const items = event.clipboardData?.items
      if (!items) {
        return
      }

      const files: Array<File> = []
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) {
            files.push(file)
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault()
        event.stopPropagation()
        attachments.add(files)
      }
    },
    [attachments, disabled]
  )

  const handleDragOverCapture = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes(ARTIFACT_MENTION_DRAG_MIME)) {
        return
      }

      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    []
  )

  const handleDropCapture = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      const payload = parseArtifactMentionPayload(
        event.dataTransfer.getData(ARTIFACT_MENTION_DRAG_MIME)
      )
      if (!payload) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      insertArtifactMention(payload)
    },
    [insertArtifactMention]
  )

  useEffect(() => {
    const onDragOver = (event: DragEvent) => {
      if (event.defaultPrevented) {
        return
      }

      if (!event.dataTransfer?.types.includes(ARTIFACT_MENTION_DRAG_MIME)) {
        return
      }

      if (!isValidArtifactMentionDropTarget(event.target)) {
        return
      }

      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    }

    const onDrop = (event: DragEvent) => {
      if (event.defaultPrevented) {
        return
      }

      if (!isValidArtifactMentionDropTarget(event.target)) {
        return
      }

      const payload = parseArtifactMentionPayload(
        event.dataTransfer?.getData(ARTIFACT_MENTION_DRAG_MIME)
      )
      if (!payload) {
        return
      }

      event.preventDefault()
      insertArtifactMention(payload)
    }

    document.addEventListener('dragover', onDragOver)
    document.addEventListener('drop', onDrop)

    return () => {
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('drop', onDrop)
    }
  }, [insertArtifactMention])

  const inputClassName = cn(
    PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME,
    'outline-none',
    PROMPT_INPUT_TEXTAREA_HEIGHT_CAP_CLASSNAME,
    disabled && 'cursor-not-allowed opacity-50',
    className
  )

  if (disabled) {
    return (
      <div className="relative w-full flex-1">
        <InputGroupTextarea
          className={cn(
            PROMPT_INPUT_PLAIN_TEXTAREA_CLASSNAME,
            shimmerPlaceholder && 'placeholder:text-transparent',
            className
          )}
          disabled
          name="message"
          onChange={onChange}
          placeholder={placeholder}
          value={externalValue}
          data-testid={dataTestId}
          aria-label={ariaLabel}
        />
        {shimmerPlaceholder && !externalValue ? (
          <div
            className="pointer-events-none absolute top-1 right-0 left-2 select-none px-3 pt-3"
            aria-hidden="true"
          >
            <TextShimmer
              className="text-sm [--base-color:var(--color-neutral-400)] dark:[--base-color:var(--color-neutral-400)]"
              spread={50}
            >
              {placeholder}
            </TextShimmer>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      ref={setRootRef}
      className="relative w-full flex-1"
      data-testid={dataTestId}
      onBlur={releaseCaretHold}
      onKeyUp={syncCaretHold}
      onPointerUp={syncCaretHold}
    >
      <textarea
        readOnly
        ref={hiddenTextareaRef}
        name="message"
        value={plainText}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
      <div
        onKeyDownCapture={handleKeyDownCapture}
        onPasteCapture={handlePasteCapture}
        onDragOverCapture={handleDragOverCapture}
        onDropCapture={handleDropCapture}
      >
        <SmartTextbox
          value={segments}
          onChange={handleSegmentsChange}
          onFetchMentions={handleFetchMentions}
          onFetchSuggestions={
            enableAutocomplete && !isDictating
              ? handleFetchSuggestions
              : undefined
          }
          autocompleteDelay={300}
          multiline
          placeholder={shimmerPlaceholder ? '' : placeholder}
          classNames={{
            input: inputClassName,
            ...FUDE_PROMPT_INPUT_CLASSNAMES,
          }}
          styles={FUDE_PROMPT_INPUT_STYLES}
        />
      </div>
      {shimmerPlaceholder && !plainText ? (
        <div
          className="pointer-events-none absolute top-[4px] right-0 left-2 select-none px-3 pt-3"
          aria-hidden="true"
        >
          <TextShimmer
            className="text-sm [--base-color:var(--color-neutral-400)] dark:[--base-color:var(--color-neutral-400)]"
            spread={50}
          >
            {placeholder}
          </TextShimmer>
        </div>
      ) : null}
    </div>
  )
}
